import React, { useState, useEffect } from 'react';

interface DebugLog {
  timestamp: string;
  level: 'info' | 'success' | 'error' | 'warning';
  message: string;
}

interface WebRTCDebuggerProps {
  isVisible: boolean;
  onToggle: () => void;
}

export const WebRTCDebugger: React.FC<WebRTCDebuggerProps> = ({ isVisible, onToggle }) => {
  const [logs, setLogs] = useState<DebugLog[]>([]);
  const [connectionStatus, setConnectionStatus] = useState<string>('idle');

  useEffect(() => {
    // Intercept console.log messages for WebRTC debugging
    const originalLog = console.log;
    const originalError = console.error;

    const addLog = (level: 'info' | 'success' | 'error' | 'warning', ...args: any[]) => {
      const message = args.join(' ');
      // Only capture WebRTC-related messages
      if (message.includes('📡') || message.includes('🟢') || message.includes('🔵') || 
          message.includes('✅') || message.includes('❌') || message.includes('📤') || 
          message.includes('📨') || message.includes('🔄') || message.includes('WebRTC') ||
          message.includes('signaling') || message.includes('connection')) {
        
        const log: DebugLog = {
          timestamp: new Date().toLocaleTimeString(),
          level,
          message
        };
        
        setLogs(prev => [...prev.slice(-19), log]); // Keep last 20 logs
      }
    };

    console.log = (...args) => {
      originalLog(...args);
      addLog('info', ...args);
    };

    console.error = (...args) => {
      originalError(...args);
      addLog('error', ...args);
    };

    // Listen for WebRTC state changes
    const handleWebRTCStateChange = (event: CustomEvent) => {
      setConnectionStatus(event.detail.state);
      addLog('info', `🔄 WebRTC State: ${event.detail.state}`);
    };

    window.addEventListener('webrtc-state-change' as any, handleWebRTCStateChange);

    return () => {
      console.log = originalLog;
      console.error = originalError;
      window.removeEventListener('webrtc-state-change' as any, handleWebRTCStateChange);
    };
  }, []);

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'success': return 'text-green-600';
      case 'error': return 'text-red-600';
      case 'warning': return 'text-yellow-600';
      default: return 'text-blue-600';
    }
  };

  const clearLogs = () => setLogs([]);

  const copyLogs = async () => {
    const logText = logs.map(log => `[${log.timestamp}] ${log.message}`).join('\n');
    
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(logText);
        alert('Logs copied to clipboard!');
      } else {
        // Fallback for older browsers or non-secure contexts
        const textArea = document.createElement('textarea');
        textArea.value = logText;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        textArea.remove();
        alert('Logs copied to clipboard!');
      }
    } catch (err) {
      console.error('Failed to copy logs:', err);
      alert('Failed to copy logs. Please try again.');
    }
  };

  return (
    <>
      {/* Always visible debug button */}
      <button
        onClick={onToggle}
        className={`fixed bottom-4 right-4 text-white p-3 rounded-full shadow-lg text-sm transition-colors ${
          isVisible ? 'bg-red-500 hover:bg-red-600' : 'bg-blue-500 hover:bg-blue-600'
        }`}
        title={isVisible ? "Hide WebRTC Debug" : "Show WebRTC Debug"}
        style={{ zIndex: 9999 }}
      >
        {isVisible ? '❌' : '🐛'}
      </button>

      {/* Debug overlay */}
      {isVisible && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" 
          style={{ zIndex: 9998 }}
        >
          <div className="bg-white rounded-lg w-full max-w-4xl h-96 flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">WebRTC Debug Console</h3>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 rounded text-sm ${
                  connectionStatus === 'connected' ? 'bg-green-100 text-green-800' :
                  connectionStatus === 'error' ? 'bg-red-100 text-red-800' :
                  connectionStatus === 'hosting' || connectionStatus === 'joining' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {connectionStatus}
                </span>
                <button
                  onClick={copyLogs}
                  disabled={logs.length === 0}
                  className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  title="Copy all logs to clipboard"
                >
                  📋 Copy
                </button>
                <button
                  onClick={clearLogs}
                  className="px-3 py-1 bg-gray-200 rounded text-sm hover:bg-gray-300"
                >
                  Clear
                </button>
                <button
                  onClick={onToggle}
                  className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600"
                >
                  Close
                </button>
              </div>
            </div>
            
            <div className="flex-1 overflow-auto p-4 font-mono text-sm">
              {logs.length === 0 ? (
                <div className="text-gray-500 text-center mt-8">
                  No WebRTC debug messages yet. Try creating or joining a session.
                </div>
              ) : (
                <div className="space-y-1">
                  {logs.map((log, index) => (
                    <div key={index} className="flex gap-2">
                      <span className="text-gray-400 text-xs w-20 flex-shrink-0">
                        {log.timestamp}
                      </span>
                      <span className={`${getLevelColor(log.level)} flex-1 break-all`}>
                        {log.message}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="p-4 border-t bg-gray-50 text-sm text-gray-600">
              <strong>Testing Instructions:</strong> Create a session on one device, then join with the session code on another device. 
              Watch for signaling messages and connection establishment.
            </div>
          </div>
        </div>
      )}
    </>
  );
};